import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, Loader2 } from 'lucide-react';

const SPORTS = ['Football','Cricket','Badminton','Tennis','Basketball','Hockey','Volleyball','Pickleball','Table Tennis','Squash','Athletics'];
const SKILL_LEVELS = ['beginner','intermediate','advanced','pro'];
const TIMINGS = ['Early morning (5-8 AM)','Morning (8-11 AM)','Afternoon (12-4 PM)','Evening (5-8 PM)','Night (8-11 PM)'];

export default function ArenaxPlayerOnboarding() {
  const navigate = useNavigate();
  const [sports, setSports] = useState([]);
  const [skills, setSkills] = useState({});
  const [locations, setLocations] = useState('');
  const [timings, setTimings] = useState([]);
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/arenax/player/profile').then(r => {
      const p = r.data;
      setSports(p.sports_interests || []);
      setSkills(p.skill_levels || {});
      setLocations((p.preferred_locations || []).join(', '));
      setTimings(p.preferred_timings || []);
      setBio(p.bio || '');
    }).catch(() => {});
  }, []);

  const toggle = (arr, setter, val) => {
    setter(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };

  const submit = async () => {
    if (sports.length === 0) { toast.error('Pick at least one sport you love'); return; }
    setLoading(true);
    try {
      await api.post('/arenax/player/onboarding', {
        sports_interests: sports,
        skill_levels: skills,
        preferred_locations: locations.split(',').map(s => s.trim()).filter(Boolean),
        preferred_timings: timings,
        bio,
      });
      toast.success('Profile saved');
      navigate('/arenax/player');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setLoading(false); }
  };

  return (
    <ArenaxLayout>
      <section className="max-w-2xl mx-auto px-5 py-10" data-testid="arenax-onboarding">
        <div className="mb-7">
          <div className="text-[11px] tracking-[0.4em] text-cyan-300 font-bold mb-2">SETUP · 60 SECONDS</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-100">Tell us about your game</h1>
          <p className="text-sm text-slate-400 mt-1">So we can surface the right venues and players for you.</p>
        </div>

        <div className="rounded-2xl border border-cyan-500/15 p-6 space-y-6" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
          {/* Sports */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-3 block">Sports you love</label>
            <div className="flex flex-wrap gap-2">
              {SPORTS.map(s => (
                <button key={s} onClick={() => toggle(sports, setSports, s)}
                  data-testid={`arenax-onb-sport-${s.toLowerCase().replace(/ /g, '-')}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
                    sports.includes(s) ? 'bg-cyan-500 text-slate-950 shadow-lg' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-cyan-500/50'
                  }`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Skill per sport */}
          {sports.length > 0 && (
            <div>
              <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-3 block">Your skill level</label>
              <div className="space-y-2.5">
                {sports.map(s => (
                  <div key={s} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-slate-900/60 border border-slate-800">
                    <span className="text-sm font-semibold text-slate-200">{s}</span>
                    <div className="flex gap-1">
                      {SKILL_LEVELS.map(lvl => (
                        <button key={lvl} onClick={() => setSkills({ ...skills, [s]: lvl })}
                          data-testid={`arenax-onb-skill-${s.toLowerCase().replace(/ /g, '-')}-${lvl}`}
                          className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                            skills[s] === lvl ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                          }`}>{lvl}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Locations */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-2 block">Preferred areas / cities</label>
            <input type="text" value={locations} onChange={e => setLocations(e.target.value)}
              data-testid="arenax-onb-locations"
              placeholder="e.g. Indiranagar, Koramangala, HSR Layout"
              className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm" />
          </div>

          {/* Timings */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-3 block">Preferred timings</label>
            <div className="flex flex-wrap gap-2">
              {TIMINGS.map(t => (
                <button key={t} onClick={() => toggle(timings, setTimings, t)}
                  data-testid={`arenax-onb-timing-${t.split(' ')[0].toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${
                    timings.includes(t) ? 'bg-amber-500 text-slate-950' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-amber-500/50'
                  }`}>{t}</button>
              ))}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-2 block">A line about your game (optional)</label>
            <textarea value={bio} onChange={e => setBio(e.target.value)} rows={2}
              data-testid="arenax-onb-bio"
              placeholder="Striker. Available weekends. Loves competitive 7-a-side."
              className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm resize-none" />
          </div>

          <button onClick={submit} disabled={loading} data-testid="arenax-onb-submit"
            className="w-full px-4 py-3 rounded-xl font-bold text-slate-950 text-sm shadow-xl flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: 'var(--ax-primary-gradient)' }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save & Enter Hub <ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>
      </section>
    </ArenaxLayout>
  );
}
